/**
 * PartnersListPage — F-1. 시공반장님 목록
 *
 * 레이아웃은 나의 사진첩(현장 목록)과 같은 형식 — PageHeader + 검색 영역 카드 + 카드 그리드(4열).
 * 카드 구성은 Figma 카드 예시(4366:2817)를 따른다.
 *   - 키워드: 서버 조회(fetchPartners(keyword, 'CONSTRUCTION')) — SearchCombo. 자재 전용 파트너는 서버에서 뺀다
 *   - 등록일 기간·유형·작업공종: 클라이언트 필터(반장님 목록은 소규모). 작업공종은 다중선택(OR 매칭, 주·부작업 모두).
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, DateRangePicker, EmptyState, ErrorState, FilterChip,
  Page, PageHeader, SearchCombo, Skeleton, Tag,
} from '@indipia/ui-core';
import { fetchPartners, fetchPartnerMetrics, updatePartner } from '../api/partners';
import type { PartnerItem, PartnerMetrics } from '../api/partners';
import { fetchWorkTypes } from '../api/work-types';
import { useDateRangeFilter } from '@indipia/shared';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PartnerReviewDrawer } from '../../app/shell/PartnerReviewDrawer';
import {
  InfoList, InfoRow, ListCard, ListCardActions, ListCardGrid, ListCardHeader, ListCardIdentity, ListCardMetrics, StarRating,
} from '../components/ListCard';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function IcPartner() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

// 활동지역 요약 — 시군구명("서울특별시 강남구")에서 시·도만 축약("서울"), 유니크 묶음.
function regionSummary(names: string[]): string {
  const sidos = Array.from(new Set(names.map((n) => {
    const head = (n || '').split(' ')[0];
    return head.replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '');
  }).filter(Boolean)));
  if (sidos.length === 0) return '-';
  return sidos.join(' · ');
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────

export function PartnersListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: workTypeList = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });
  const codeNameMap = useMemo(
    () => new Map(workTypeList.map((w) => [w.workTypeCode, w.workTypeName])),
    [workTypeList],
  );

  const [deleteTarget, setDeleteTarget] = useState<PartnerItem | null>(null);
  const [reviewTarget, setReviewTarget] = useState<PartnerItem | null>(null);

  // 검색(서버) — 입력 디바운스로 실시간 조회, 버튼/Enter는 즉시
  const [searchInput, setSearchInput] = useState('');
  const [keyword,     setKeyword]     = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  useEffect(() => { setKeyword(debouncedSearch.trim()); }, [debouncedSearch]);

  // 등록일 기간 필터(클라이언트, createdAt 기준) — 기본 미지정(전체)
  const { dateRange, setDateRange, dateFrom, dateTo, reset: resetDateRange } = useDateRangeFilter();

  // 작업공종 필터(클라이언트, 다중선택 OR 매칭)
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);

  // 파트너 유형 필터(클라이언트) — 미지정(null)은 개인으로 간주
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'PERSON' | 'COMPANY'>('ALL');

  // 파트너 원장은 시공·자재가 하나라 role=CONSTRUCTION(시공·둘다)으로 받는다 — 자재 전용 파트너가 이 목록에 섞이지 않게
  const { data: partners = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['partners', 'CONSTRUCTION', keyword],
    queryFn: () => fetchPartners(keyword || undefined, 'CONSTRUCTION'),
  });

  // 협업·일정회신·보고준수·하자보고 지표 — 일정 서비스(sv-schedule) 집계를 partnerId 로 병합.
  // 조회 실패 시 지표는 모두 생략하고 목록은 정상 표시한다.
  const { data: metricList } = useQuery({
    queryKey: ['partner-metrics'],
    queryFn: fetchPartnerMetrics,
    retry: 1,
  });
  const metricsById = useMemo(() => {
    const map = new Map<number, PartnerMetrics>();
    for (const m of metricList ?? []) map.set(m.partnerId, m);
    return map;
  }, [metricList]);

  const deleteMutation = useMutation({
    mutationFn: (partnerId: number) =>
      updatePartner(partnerId, { action: 'delete' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setDeleteTarget(null);
    },
  });

  // ── 클라이언트 필터 (등록일 + 작업공종 + 유형) ─────────────────────────────
  const filtered = useMemo(() => partners.filter((p) => {
    // createdAt 미제공(BE 배포 전) 시 날짜 조건은 건너뜀 — 목록이 비지 않도록 방어
    if ((dateFrom || dateTo) && p.createdAt) {
      const d = p.createdAt.slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo   && d > dateTo)   return false;
    }
    if (selectedSpecialties.length > 0) {
      // 작업공종 — 주작업(specialtyTypes) 또는 부작업(subWorkTypes) 어느 쪽이든 일치하면 통과
      const codes = (p.specialtyTypes ?? '').split(',').map((v) => v.trim()).filter(Boolean);
      const all = [...codes, ...(p.subWorkTypes ?? [])];
      if (!all.some((c) => selectedSpecialties.includes(c))) return false;
    }
    if (typeFilter !== 'ALL') {
      const isCompany = p.partnerType === 'COMPANY';
      if (typeFilter === 'COMPANY' ? !isCompany : isCompany) return false;
    }
    return true;
  }), [partners, dateFrom, dateTo, selectedSpecialties, typeFilter]);

  const hasActiveFilters = !!keyword || !!dateFrom || !!dateTo || selectedSpecialties.length > 0 || typeFilter !== 'ALL';

  function clearFilters() {
    setKeyword('');
    setSearchInput('');
    resetDateRange();
    setSelectedSpecialties([]);
    setTypeFilter('ALL');
  }

  function toggleSpecialty(code: string) {
    setSelectedSpecialties((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  function getSpecialtyNames(specialtyTypes: string | null): string[] {
    if (!specialtyTypes) return [];
    return specialtyTypes.split(',').map((v) => {
      const trimmed = v.trim();
      return codeNameMap.get(trimmed) ?? trimmed;
    });
  }

  return (
    <Page width="xl">
      <PageHeader
        title="시공반장님 목록"
        subtitle="등록된 시공반장님을 확인하고 관리하세요"
        extra={
          <Button type="button" variant="primary" onClick={() => navigate('/partners/new')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            신규 등록
          </Button>
        }
      />

      <div className="flex flex-col gap-5">

        {/* 검색 영역 — 나의 사진첩 현장 목록과 같은 카드 묶음 (①키워드·등록일 ②유형 ③작업공종) */}
        <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface-raised p-5">
          <div className="flex flex-wrap items-center gap-2">
            <SearchCombo
              className="min-w-60 flex-1"
              value={searchInput}
              onChange={(v) => setSearchInput(v.slice(0, 50))}
              onSearch={() => setKeyword(searchInput.trim())}
              onClear={() => { setSearchInput(''); setKeyword(''); }}
              maxLength={50}
              placeholder="이름 · 연락처 · 상호명 검색"
              aria-label="시공반장님 검색"
            />
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder="등록일 전체"
              aria-label="등록일 기간"
            />
            {hasActiveFilters && (
              <Button type="button" variant="etc" onClick={clearFilters} className="shrink-0">
                초기화
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="w-16 shrink-0 text-caption font-semibold text-text-secondary">유형</span>
            <FilterChip active={typeFilter === 'ALL'} onClick={() => setTypeFilter('ALL')}>전체</FilterChip>
            <FilterChip active={typeFilter === 'PERSON'} onClick={() => setTypeFilter('PERSON')}>개인</FilterChip>
            <FilterChip active={typeFilter === 'COMPANY'} onClick={() => setTypeFilter('COMPANY')}>업체</FilterChip>
          </div>

          <div className="flex flex-wrap items-start gap-1.5">
            <span className="w-16 shrink-0 pt-1 text-caption font-semibold text-text-secondary">작업공종</span>
            <div className="flex min-w-60 flex-1 flex-wrap items-center gap-1.5">
              <FilterChip active={selectedSpecialties.length === 0} onClick={() => setSelectedSpecialties([])}>
                전체
              </FilterChip>
              {workTypeList.map((w) => (
                <FilterChip
                  key={w.workTypeCode}
                  active={selectedSpecialties.includes(w.workTypeCode)}
                  onClick={() => toggleSpecialty(w.workTypeCode)}
                >
                  {w.workTypeName}
                </FilterChip>
              ))}
            </div>
          </div>

          <p className="flex items-center gap-1 text-caption text-text-muted">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            기간 검색 시 반장님 등록일 기준으로 검색됩니다.
          </p>
        </div>

        {isLoading && (
          <ListCardGrid>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
          </ListCardGrid>
        )}

        {!isLoading && isError && (
          <ErrorState description="시공반장님 목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요." onRetry={() => void refetch()} />
        )}

        {!isLoading && !isError && filtered.length === 0 && hasActiveFilters && (
          <EmptyState
            icon={<IcPartner />}
            title="조회 결과가 없습니다"
            description="검색어·기간·유형·작업공종 조건에 맞는 반장님이 없어요."
            action={<Button type="button" variant="secondary" onClick={clearFilters}>필터 초기화</Button>}
          />
        )}

        {!isLoading && !isError && filtered.length === 0 && !hasActiveFilters && (
          <EmptyState
            icon={<IcPartner />}
            title="등록된 시공반장님이 없습니다"
            description="첫 반장님을 등록하고 시공 파트너를 관리해 보세요."
            action={<Button type="button" variant="primary" onClick={() => navigate('/partners/new')}>+ 첫 반장님 등록</Button>}
          />
        )}

        {/* 카드 그리드 — 4열 고정, 카드 구성은 Figma 카드 예시(4366:2817) */}
        {!isLoading && !isError && filtered.length > 0 && (
          <ListCardGrid>
            {filtered.map((partner) => {
              const mainTrades = getSpecialtyNames(partner.specialtyTypes);
              const subTrades = (partner.subWorkTypes ?? []).map((c) => codeNameMap.get(c) ?? c);
              const hasBiz = partner.partnerBizYn === 'Y';
              const typeLabel = partner.partnerType === 'FOREMAN' ? '개인'
                : partner.partnerType === 'COMPANY' ? '업체' : null;
              // 협업·일정회신·보고준수·하자보고 지표는 일정 서비스 집계 병합값(null 이면 그 항목은 표시하지 않음).
              // 함께한 현장 유무는 협업 횟수(collabCount) > 0 기준. 병합 조회가 실패(metrics 없음)했을 때는 지표를 모두 생략하고
              // 별점이 있는 경우(평가 건수 > 0)에만 별점만 보여준다.
              const metrics = metricsById.get(partner.partnerId);
              const hasHistory = metrics ? metrics.collabCount > 0 : partner.reviewCount > 0;
              const region = regionSummary((partner.sigungus ?? []).map((s) => s.name));

              const badges = [
                typeLabel && <Badge key="type" status="info">{typeLabel}</Badge>,
                hasBiz && <Badge key="biz" status="info">사업자</Badge>,
                partner.dealStatus === 'SUSPENDED' && <Badge key="deal" status="warning">거래중지</Badge>,
                partner.commYn === 'N' && <Badge key="comm" status="warning">알림톡 수신 안 함</Badge>,
              ];
              const hasBadge = badges.some(Boolean);

              return (
                <ListCard key={partner.partnerId}>
                  <ListCardHeader
                    badges={badges}
                    title={partner.partnerName}
                    editLabel="반장님 편집"
                    deleteLabel="반장님 삭제"
                    onEdit={() => navigate(`/partners/${partner.partnerId}/edit`, { state: { partner } })}
                    onDelete={() => setDeleteTarget(partner)}
                  />

                  <ListCardIdentity
                    name={partner.partnerName}
                    hideName={!hasBadge}
                    phone={partner.mobile}
                    lead={hasBiz ? partner.partnerBizName : null}
                  />

                  {/* 작업공종 — 주(강조) / 부 */}
                  {(mainTrades.length > 0 || subTrades.length > 0) && (
                    <div className="flex flex-wrap gap-2">
                      {mainTrades.map((name) => <Tag key={`m-${name}`} className="!border-transparent !bg-point-subtle !text-point">{name}</Tag>)}
                      {subTrades.map((name) => <Tag key={`s-${name}`}>{name}</Tag>)}
                    </div>
                  )}

                  {/* 지표(★평점·협업·회신·보고준수·하자) */}
                  <ListCardMetrics>
                    {!hasHistory && <span>아직 함께한 현장이 없어요</span>}
                    {(hasHistory || partner.reviewCount > 0) && (
                      <span className="flex flex-wrap items-center gap-x-4">
                        <StarRating rating={partner.avgRating ?? 0} count={partner.reviewCount} />
                        {metrics && <span className="text-point">협업 <b className="font-bold">{metrics.collabCount}회</b></span>}
                      </span>
                    )}
                    {metrics?.replyWithinTwoHoursPercent != null && (
                      <span>일정회신 2시간 내 <b className="font-bold text-text">{metrics.replyWithinTwoHoursPercent}%</b></span>
                    )}
                    {metrics?.reportComplianceRate != null && (
                      <span>보고준수 <b className="font-bold text-text">{metrics.reportComplianceRate}%</b></span>
                    )}
                    {metrics?.defectReportRate != null && (
                      <span>하자보고 <b className="font-bold text-text">{metrics.defectReportRate}%</b></span>
                    )}
                  </ListCardMetrics>

                  <InfoList>
                    <InfoRow label="활동지역">{region}</InfoRow>
                    <InfoRow label="등록일">{formatDate(partner.createdAt)}</InfoRow>
                    <InfoRow label="최근시공현장">
                      {partner.lastConstructionSiteName
                        ? `${partner.lastConstructionSiteName} (${formatDate(partner.lastConstructionDate)})`
                        : '-'}
                    </InfoRow>
                    {partner.memo && <InfoRow label="메모"><span className="line-clamp-2" title={partner.memo}>{partner.memo}</span></InfoRow>}
                    {partner.tags.length > 0 && (
                      <InfoRow label="태그">
                        <span className="flex flex-wrap gap-1">
                          {partner.tags.map((t) => <Tag key={t}>{t}</Tag>)}
                        </span>
                      </InfoRow>
                    )}
                  </InfoList>

                  <ListCardActions>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setReviewTarget(partner)} className="col-span-2 w-full">
                      시공이력 &amp; 평가 메모
                    </Button>
                  </ListCardActions>
                </ListCard>
              );
            })}
          </ListCardGrid>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <ConfirmDialog
          open
          message={`${deleteTarget.partnerName}님을 삭제하시겠습니까?`}
          description="삭제 후 복구할 수 없습니다."
          pendingLabel="삭제 중…"
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget.partnerId)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* 시공이력·평가 슬라이드오버 (C1/C2) */}
      {reviewTarget && (
        <PartnerReviewDrawer
          key={reviewTarget.partnerId}
          kind="partner"
          id={reviewTarget.partnerId}
          name={reviewTarget.partnerName}
          onClose={() => setReviewTarget(null)}
        />
      )}
    </Page>
  );
}
